import React from 'react';
import CodeBlock from '../components/CodeBlock';
import './GettingStarted.css';

function GettingStarted() {
  return (
    <div className="getting-started">
      <h1>Getting Started</h1>

      <section className="section">
        <h2>Installation</h2>
        <p>Install the package using npm or yarn:</p>
        <CodeBlock
          language="bash"
          code={`npm install react-native-lite-ui`}
        />
        <p>Or with yarn:</p>
        <CodeBlock
          language="bash"
          code={`yarn add react-native-lite-ui`}
        />
      </section>

      <section className="section">
        <h2>Dependencies</h2>
        <p>Make sure to install the required peer dependencies:</p>
        <CodeBlock
          language="bash"
          code={`npm install @react-native-async-storage/async-storage react-native-vector-icons lottie-react-native react-native-size-matters`}
        />
      </section>

      <section className="section">
        <h2>Setup Theme Provider</h2>
        <p>Wrap your application with ThemeProvider and configure your theme:</p>
        <CodeBlock
          language="jsx"
          code={`import React from 'react';
import { ThemeProvider } from 'react-native-lite-ui';

const themeConfig = {
  colors: {
    primary: '#007AFF',
    secondary: '#FF3B30',
    backgroundColor: '#FFFFFF',
    buttonColor: '#007AFF',
    textColor: '#000000',
    disabledColor: '#E7E8E9',
    errorColor: '#FF3B30',
  },
  themesColors: {
    light: {
      primary: '#007AFF',
      secondary: '#FF3B30',
      backgroundColor: '#FFFFFF',
      buttonColor: '#007AFF',
      textColor: '#000000',
      disabledColor: '#E7E8E9',
      errorColor: '#FF3B30',
    },
    dark: {
      primary: '#0A84FF',
      secondary: '#FF453A',
      backgroundColor: '#000000',
      buttonColor: '#0A84FF',
      textColor: '#FFFFFF',
      disabledColor: '#3A3A3C',
      errorColor: '#FF453A',
    },
  },
  fontSizes: {
    extraExtraSmall: 10,
    extraSmall: 12,
    small: 14,
    medium: 16,
    large: 18,
    extraLarge: 20,
    extraExtraLarge: 24,
  },
  fonts: {
    regular: 'Roboto-Regular',
    medium: 'Roboto-Medium',
    bold: 'Roboto-Bold',
  },
};

function App() {
  return (
    <ThemeProvider initialValues={themeConfig}>
      <YourComponents />
    </ThemeProvider>
  );
}

export default App;`}
        />
      </section>

      <section className="section">
        <h2>Using the Theme Hook</h2>
        <p>Access theme values in your components using the useTheme hook:</p>
        <CodeBlock
          language="jsx"
          code={`import { useTheme } from 'react-native-lite-ui';
import React from 'react';

function MyComponent() {
  const theme = useTheme();

  return (
    <View style={{
      backgroundColor: theme.colors.backgroundColor,
      padding: 20,
    }}>
      <Text style={{
        color: theme.colors.textColor,
        fontSize: theme.fontSizes.medium,
        fontFamily: theme.fonts.regular,
      }}>
        Hello World
      </Text>
    </View>
  );
}`}
        />
      </section>

      <section className="section">
        <h2>Theme Switching</h2>
        <p>Users can switch between light and dark modes:</p>
        <CodeBlock
          language="jsx"
          code={`import { useTheme } from 'react-native-lite-ui';
import { Button } from 'react-native';

function ThemeSwitcher() {
  const { toggleTheme, themeMode } = useTheme();

  return (
    <Button
      title={\`Current: \${themeMode}\`}
      onPress={() => toggleTheme()}
    />
  );
}`}
        />
      </section>

      <section className="section">
        <h2>Basic Component Usage</h2>
        <p>Here's a simple example using multiple components:</p>
        <CodeBlock
          language="jsx"
          code={`import React, { useState } from 'react';
import {
  Text,
  Button,
  TextInput,
  Switch,
  CustomCheckbox,
} from 'react-native-lite-ui';
import { View } from 'react-native';

function SignUpForm() {
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [notifications, setNotifications] = useState(false);

  const handleSubmit = () => {
    if (!email || !agreed) {
      alert('Please fill all fields');
      return;
    }
    console.log('Form submitted', { email, agreed, notifications });
  };

  return (
    <View style={{ padding: 20, gap: 16 }}>
      <Text mode="bold" fontSize="large">
        Create Account
      </Text>

      <TextInput
        placeholder="Enter email"
        value={email}
        onChangeText={setEmail}
      />

      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Text>Enable Notifications</Text>
        <Switch
          isOn={notifications}
          onToggle={setNotifications}
        />
      </View>

      <CustomCheckbox
        label="I agree to terms"
        checked={agreed}
        onChange={() => setAgreed(!agreed)}
      />

      <Button
        title="Sign Up"
        onPress={handleSubmit}
        disabled={!agreed}
      />
    </View>
  );
}`}
        />
      </section>

      <section className="section">
        <h2>Next Steps</h2>
        <p>Now that you're set up, explore the components:</p>
        <ul className="next-steps-list">
          <li><strong>Text</strong> - Display styled text content</li>
          <li><strong>Button</strong> - Interactive button component</li>
          <li><strong>TextInput</strong> - User input with validation</li>
          <li><strong>Switch</strong> - Toggle control</li>
          <li><strong>Checkbox</strong> - Checkbox input</li>
          <li><strong>Toast</strong> - Notification messages</li>
          <li><strong>Alert</strong> - Modal alerts</li>
          <li><strong>Dialog</strong> - Customizable modals</li>
        </ul>
        <p>Click on any component in the sidebar to view detailed documentation and examples.</p>
      </section>

      <section className="section tips">
        <h2>💡 Tips</h2>
        <div className="tip">
          <p><strong>Tip 1:</strong> Always wrap your app with ThemeProvider for consistent theming.</p>
        </div>
        <div className="tip">
          <p><strong>Tip 2:</strong> Use the useTheme hook to access colors and fonts throughout your app.</p>
        </div>
        <div className="tip">
          <p><strong>Tip 3:</strong> All components support theme-aware styling automatically.</p>
        </div>
        <div className="tip">
          <p><strong>Tip 4:</strong> Toast, Alert, and Dialog use static methods like Toast.show().</p>
        </div>
      </section>
    </div>
  );
}

export default GettingStarted;
